import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useLocation } from 'react-router-dom';

const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};


export const ONBOARDING_STEPS = {
  DASHBOARD: 'dashboard',
  CONSULTING: 'consulting',
  CHAT: 'chat',
  RESUME_PREVIEW: 'resume_preview',
  RESUME_EDIT: 'resume_edit',
  COMPLETED: 'completed'
};

export const OnboardingProvider = ({ children }) => {
  const { user, profile } = useAuth();
  const location = useLocation();
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(ONBOARDING_STEPS.DASHBOARD);
  const [isOnboardingCompleted, setIsOnboardingCompleted] = useState(false);
  const [shouldShowWelcome, setShouldShowWelcome] = useState(false);
  const [activePage, setActivePage] = useState('');


  useEffect(() => {
    const pathname = location.pathname;
    let newActivePage = '';
    
    if (pathname === '/dashboard') {
      newActivePage = 'dashboard';
    } else if (pathname === '/consulting') {
      newActivePage = 'consulting';
    } else if (pathname.startsWith('/resume')) {
      newActivePage = 'resume-edit';
    }
    
    setActivePage(newActivePage);
  }, [location.pathname]);


  useEffect(() => {
    if (user) {
      const completedKey = `onboarding_completed_${user.id}`;
      const isCompleted = localStorage.getItem(completedKey) === 'true';
      
      setIsOnboardingCompleted(isCompleted);
      

      if (!isCompleted) {
        setShouldShowWelcome(true);
      }
    }
  }, [user]);

  const startOnboarding = (step = ONBOARDING_STEPS.DASHBOARD) => {
    setCurrentStep(step);
    setIsOnboardingActive(true);
    setShouldShowWelcome(false);
  };

  const completeOnboarding = () => {
    setIsOnboardingActive(false);
    setIsOnboardingCompleted(true);
    setShouldShowWelcome(false);
    
    if (user) {
      const completedKey = `onboarding_completed_${user.id}`;
      localStorage.setItem(completedKey, 'true');
    }
  };

  const skipOnboarding = () => {
    setIsOnboardingActive(false);
    setShouldShowWelcome(false);
    
    if (user) {
      const completedKey = `onboarding_completed_${user.id}`;
      localStorage.setItem(completedKey, 'true');
    }
  };

  const resetOnboarding = () => {
    setIsOnboardingCompleted(false);
    setIsOnboardingActive(false);
    setCurrentStep(ONBOARDING_STEPS.DASHBOARD);
    setShouldShowWelcome(true);
    
    if (user) {
      const completedKey = `onboarding_completed_${user.id}`;
      localStorage.removeItem(completedKey);
    }
  };

  const goToStep = (step) => {
    setCurrentStep(step);
  };

  const dismissWelcome = () => {
    setShouldShowWelcome(false);
  };

  const value = {
    isOnboardingActive,
    currentStep,
    isOnboardingCompleted,
    shouldShowWelcome,
    activePage,
    startOnboarding,
    completeOnboarding,
    skipOnboarding,
    resetOnboarding,
    goToStep,
    dismissWelcome,
    ONBOARDING_STEPS
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};